/// <reference path="../nativewind-augmentation.d.ts" />
// Copyright (c) Ping. All rights reserved.
//
// Animated wallpaper for the new auth screens. Uses the Figma raster
// (file gtBsWqJJcI6ZBuBv51RU35 nodes 3067:54754 / 3067:54755) — a
// wireframe sculpture rendered with alpha — anchored at the top-center
// of the viewport. On web the image breathes (slow rotate + scale +
// opacity pulse, 30 s loop) and fades into the dark base via a CSS
// mask. macOS renders the same image statically (no CSS animation
// engine) so the visual still matches.
//
// The PNG keeps its alpha channel — see `assets/auth-wallpaper.png`
// (re-exported from the original 4096² source) — so the wireframe
// renders cleanly over the dark page background.

import { useEffect } from 'react';
import { Image, type ImageSourcePropType, Platform, View } from 'react-native';
import authWallpaper from './assets/auth-wallpaper.png';

const KEYFRAMES_STYLE_ID = 'ping-auth-wallpaper-keyframes';
const KEYFRAMES_CSS = `
@keyframes ping-auth-wallpaper-breathe {
  0%, 100% {
    transform: translateX(-50%) rotate(-1.5deg) scale(1);
    opacity: 0.9;
  }
  50% {
    transform: translateX(-50%) rotate(1.5deg) scale(1.03);
    opacity: 1;
  }
}
@media (prefers-reduced-motion: reduce) {
  [data-ping-auth-wallpaper] {
    animation: none !important;
    transform: translateX(-50%) !important;
  }
}
`;

function ensureKeyframesInjected(): void {
	if (typeof document === 'undefined') return;
	if (document.getElementById(KEYFRAMES_STYLE_ID)) return;
	const styleEl = document.createElement('style');
	styleEl.id = KEYFRAMES_STYLE_ID;
	styleEl.textContent = KEYFRAMES_CSS;
	document.head.appendChild(styleEl);
}

export function AuthWallpaper(): JSX.Element {
	const source: ImageSourcePropType =
		typeof (authWallpaper as unknown) === 'string'
			? { uri: authWallpaper as unknown as string }
			: (authWallpaper as unknown as ImageSourcePropType);

	useEffect(() => {
		if (Platform.OS === 'web') ensureKeyframesInjected();
	}, []);

	const onWeb = Platform.OS === 'web';

	return (
		<View
			accessibilityElementsHidden
			pointerEvents="none"
			// `absolute inset-0` fills the auth layout; `bg-light-surface-app`
			// / `dark:bg-dark-surface-app` keeps the canvas painted in light
			// mode (where the wireframe wallpaper is hidden via the
			// `light:hidden` class on the <Image> below).
			className="absolute inset-0 overflow-hidden bg-light-surface-app dark:bg-dark-surface-app"
		>
			<Image
				source={source}
				accessibilityIgnoresInvertColors
				resizeMode={onWeb ? 'contain' : 'contain'}
				// `hidden dark:block` makes the wireframe show only in dark
				// mode; in light mode the wallpaper hides and the `surface-app`
				// (white) parent on `AuthLayout` shows through cleanly.
				className="hidden dark:block"
				{...(onWeb ? ({ 'data-ping-auth-wallpaper': true } as object) : {})}
				style={
					onWeb
						? ({
								// Anchor far above the viewport so only the bottom
								// third of the wireframe peeks at the top — mirrors
								// the Figma frame where the image extends ~2/3
								// above the visible 1920×1080 viewport.
								position: 'absolute',
								top: '-75vw',
								left: '50%',
								width: '100vw',
								height: '100vw',
								transform: 'translateX(-50%)',
								animation: 'ping-auth-wallpaper-breathe 30s ease-in-out infinite',
								willChange: 'transform, opacity',
								// Soft mask so the visible slice fades into the
								// dark base as it moves down the screen.
								maskImage:
									'linear-gradient(to bottom, rgba(0,0,0,1) 65%, rgba(0,0,0,0.5) 85%, rgba(0,0,0,0) 100%)',
								WebkitMaskImage:
									'linear-gradient(to bottom, rgba(0,0,0,1) 65%, rgba(0,0,0,0.5) 85%, rgba(0,0,0,0) 100%)',
							} as unknown as object)
						: {
								position: 'absolute',
								top: -520,
								alignSelf: 'center',
								width: 800,
								height: 800,
								opacity: 0.9,
							}
				}
			/>
		</View>
	);
}
